import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BaseAuthenticateResponse } from '../Models';
import { RedirectStateService } from './redirect-state.service';


@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private redirectStateService: RedirectStateService
  ) {}

  /**
   * Safely parse JSON string, return null if invalid
   */
  private safeParseJSON(raw: string | null): any {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (err) {
      return null;
    }
  }

  /**
   * Return origin (scheme://host[:port]) for document.referrer or empty string.
   */
  referrerOrigin(): string {
    const cachedOrigin = this.redirectStateService.get().origin;
    if (cachedOrigin) {
      return cachedOrigin;
    }
    if (!isPlatformBrowser(this.platformId)) {
      return '';
    }

    const ref = document.referrer;
    if (!ref) return '';
    try {
      return new URL(ref).origin;
    } catch (err) {
      return '';
    }
  }

  /**
   * Read token object from localStorage (key: 'X-Seech-Token'). Returns null if missing or invalid.
   */
  getStoredToken(): BaseAuthenticateResponse | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const raw = localStorage.getItem('X-Seech-Token');
    const token = this.safeParseJSON(raw);
    if (!token) return null;

    // Validate strictly against BaseAuthenticateResponse
    const hasId = typeof token.id === 'string' && token.id.length > 0;
    const hasAccessToken = typeof token.accessToken === 'string' && token.accessToken.length > 0;
    const hasExpires = token.atExpiresOn !== undefined && token.atExpiresOn !== null;

    if (!hasId || !hasAccessToken || !hasExpires) return null;
    return token;
  }

  /**
   * Return true when a valid (non-expired) token exists per BaseAuthenticateResponse.
   */
  isAuthenticated(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      const token = this.getStoredToken();
      if (!token) return false;

      const expiresValue = token.atExpiresOn;
      
      // Handle both ISO string and Date object formats
      let expiresDate: Date;
      if (typeof expiresValue === 'string') {
        expiresDate = new Date(expiresValue);
      } else if (expiresValue instanceof Date) {
        expiresDate = expiresValue;
      } else {
        return false;
      }

      const expiresMs = expiresDate.getTime();
      if (Number.isNaN(expiresMs)) return false;

      return Date.now() < expiresMs;
    } catch (err) {
      return false;
    }
  }

  /**
   * Store token in localStorage
   */
  storeToken(token: BaseAuthenticateResponse): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.setItem('X-Seech-Token', JSON.stringify(token));
  }

  /**
   * Remove token from localStorage
   */
  removeToken(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.removeItem('X-Seech-Token');
  }

  /**
   * Get token expiration date
   */
  getTokenExpiration(): Date | null {
    const token = this.getStoredToken();
    if (!token) return null;

    const expiresValue = token.atExpiresOn;
    if (typeof expiresValue === 'string') {
      return new Date(expiresValue);
    } else if (expiresValue instanceof Date) {
      return expiresValue;
    }
    return null;
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }

    const expiration = this.getTokenExpiration();
    if (!expiration) return true;

    return Date.now() >= expiration.getTime();
  }
}
